import { motion } from 'framer-motion';
import { Award, Target, Heart, TrendingUp, Users, Globe } from 'lucide-react';
import { Layout, useLanguage } from '@/components/Layout';
import { IMAGES } from '@/assets/images';
import { translations, type Language } from '@/lib/index';

export default function About() {
  const { language } = useLanguage();
  const t = translations[language];

  const stats = [
    { value: '20+', label: t.expertise.years },
    { value: '18', label: t.expertise.countries },
    { value: '3', label: 'Continents' }
  ];

  const getValues = () => {
    const valueTranslations: Record<Language, Array<{icon: any, title: string, description: string}>> = {
      fr: [
        { icon: Award, title: 'Excellence', description: 'Nous visons l\'excellence dans chaque projet et chaque interaction avec nos clients.' },
        { icon: Heart, title: 'Intégrité', description: 'Nous opérons avec transparence et honnêteté dans toutes nos relations professionnelles.' },
        { icon: Target, title: 'Expertise', description: 'Notre connaissance approfondie des réglementations douanières mondiales est notre force.' },
        { icon: TrendingUp, title: 'Réactivité', description: 'Nous répondons rapidement aux besoins de nos clients avec des solutions efficaces.' },
        { icon: Users, title: 'Engagement', description: 'Nous nous engageons pleinement dans la réussite des projets de nos clients.' },
        { icon: Globe, title: 'Vision Globale', description: 'Notre présence sur trois continents nous donne une perspective internationale unique.' }
      ],
      en: [
        { icon: Award, title: 'Excellence', description: 'We aim for excellence in every project and every interaction with our clients.' },
        { icon: Heart, title: 'Integrity', description: 'We operate with transparency and honesty in all our professional relationships.' },
        { icon: Target, title: 'Expertise', description: 'Our deep knowledge of global customs regulations is our strength.' },
        { icon: TrendingUp, title: 'Responsiveness', description: 'We respond quickly to our clients\' needs with effective solutions.' },
        { icon: Users, title: 'Commitment', description: 'We are fully committed to the success of our clients\' projects.' },
        { icon: Globe, title: 'Global Vision', description: 'Our presence on three continents gives us a unique international perspective.' }
      ],
      es: [
        { icon: Award, title: 'Excelencia', description: 'Buscamos la excelencia en cada proyecto y cada interacción con nuestros clientes.' },
        { icon: Heart, title: 'Integridad', description: 'Operamos con transparencia y honestidad en todas nuestras relaciones profesionales.' },
        { icon: Target, title: 'Experiencia', description: 'Nuestro profundo conocimiento de las regulaciones aduaneras globales es nuestra fortaleza.' },
        { icon: TrendingUp, title: 'Capacidad de respuesta', description: 'Respondemos rápidamente a las necesidades de nuestros clientes con soluciones efectivas.' },
        { icon: Users, title: 'Compromiso', description: 'Estamos plenamente comprometidos con el éxito de los proyectos de nuestros clientes.' },
        { icon: Globe, title: 'Visión Global', description: 'Nuestra presencia en tres continentes nos da una perspectiva internacional única.' }
      ],
      zh: [
        { icon: Award, title: '卓越', description: '我们在每个项目和与客户的每次互动中都追求卓越。' },
        { icon: Heart, title: '诚信', description: '我们在所有专业关系中以透明和诚实的方式运营。' },
        { icon: Target, title: '专业知识', description: '我们对全球海关法规的深入了解是我们的优势。' },
        { icon: TrendingUp, title: '响应能力', description: '我们快速响应客户需求，提供有效的解决方案。' },
        { icon: Users, title: '承诺', description: '我们全力以赴地致力于客户项目的成功。' },
        { icon: Globe, title: '全球视野', description: '我们在三大洲的存在为我们提供了独特的国际视角。' }
      ],
      ar: [
        { icon: Award, title: 'التميز', description: 'نسعى للتميز في كل مشروع وكل تفاعل مع عملائنا.' },
        { icon: Heart, title: 'النزاهة', description: 'نعمل بشفافية وصدق في جميع علاقاتنا المهنية.' },
        { icon: Target, title: 'الخبرة', description: 'معرفتنا العميقة باللوائح الجمركية العالمية هي قوتنا.' },
        { icon: TrendingUp, title: 'الاستجابة', description: 'نستجيب بسرعة لاحتياجات عملائنا بحلول فعالة.' },
        { icon: Users, title: 'الالتزام', description: 'نحن ملتزمون تمامًا بنجاح مشاريع عملائنا.' },
        { icon: Globe, title: 'الرؤية العالمية', description: 'وجودنا في ثلاث قارات يمنحنا منظورًا دوليًا فريدًا.' }
      ]
    };
    return valueTranslations[language];
  };
  
  const values = getValues();

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gradient">
                {t.about.title}
              </h1>
              <p className="text-xl text-muted-foreground">
                {t.about.subtitle}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={IMAGES.VALDEZ_PORT_TEAM}
                  alt="M. Hamir Valdez - Expert en douane et transit sur le terrain portuaire"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-4xl font-bold mb-6">{t.about.title}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  {t.about.description}
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {language === 'fr' && 'Avec plus de deux décennies d\'expérience dans les opérations douanières internationales, nous avons développé une expertise unique qui couvre l\'Asie, l\'Europe et l\'Afrique. Notre approche personnalisée et notre connaissance approfondie des réglementations locales nous permettent d\'offrir des solutions sur mesure à nos clients.'}
                  {language === 'en' && 'With over two decades of experience in international customs operations, we have developed unique expertise covering Asia, Europe and Africa. Our personalized approach and deep knowledge of local regulations allow us to offer tailored solutions to our clients.'}
                  {language === 'es' && 'Con más de dos décadas de experiencia en operaciones aduaneras internacionales, hemos desarrollado una experiencia única que cubre Asia, Europa y África. Nuestro enfoque personalizado y nuestro profundo conocimiento de las regulaciones locales nos permiten ofrecer soluciones a medida a nuestros clientes.'}
                  {language === 'zh' && '凭借超过20年的国际海关业务经验，我们已经开发出涵盖亚洲、欧洲和非洲的独特专业知识。我们的个性化方法和对当地法规的深入了解使我们能够为客户提供量身定制的解决方案。'}
                  {language === 'ar' && 'مع أكثر من عقدين من الخبرة في العمليات الجمركية الدولية، طورنا خبرة فريدة تغطي آسيا وأوروبا وأفريقيا. يتيح لنا نهجنا الشخصي ومعرفتنا العميقة باللوائح المحلية تقديم حلول مخصصة لعملائنا.'}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">{t.about.founder}</h2>
                <p className="text-xl text-primary font-semibold">{t.about.founderTitle}</p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="order-2 md:order-1"
                >
                  <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      {language === 'fr' && 'Monsieur Hamir Valdez possède plus de 20 ans d\'expérience dans le domaine des solutions douanières et du transit international. Sa carrière l\'a mené à travers les continents, où il a développé une expertise approfondie des réglementations douanières dans de nombreux pays.'}
                      {language === 'en' && 'Mr. Hamir Valdez has over 20 years of experience in customs solutions and international transit. His career has taken him across continents, where he has developed deep expertise in customs regulations in many countries.'}
                      {language === 'es' && 'El Sr. Hamir Valdez tiene más de 20 años de experiencia en soluciones aduaneras y tránsito internacional. Su carrera lo ha llevado a través de continentes, donde ha desarrollado una profunda experiencia en regulaciones aduaneras en muchos países.'}
                      {language === 'zh' && 'Hamir Valdez 先生在海关解决方案和国际运输领域拥有超过20年的经验。他的职业生涯跨越各大洲，在许多国家的海关法规方面积累了深厚的专业知识。'}
                      {language === 'ar' && 'يمتلك السيد حمير فالديز أكثر من 20 عامًا من الخبرة في الحلول الجمركية والعبور الدولي. أخذته مسيرته المهنية عبر القارات، حيث طور خبرة عميقة في اللوائح الجمركية في العديد من البلدان.'}
                    </p>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      {language === 'fr' && 'Son parcours professionnel couvre des villes majeures en Asie (Bangkok, Hong Kong, Jakarta, Manila, Guangzhou), en Europe (Istanbul, Paris, Madrid, Bruxelles, Londres, Milan) et en Afrique (Nairobi, Bujumbura, Kinshasa, Kampala, Casablanca, Lagos, Cape Town).'}
                      {language === 'en' && 'His professional journey covers major cities in Asia (Bangkok, Hong Kong, Jakarta, Manila, Guangzhou), Europe (Istanbul, Paris, Madrid, Brussels, London, Milan) and Africa (Nairobi, Bujumbura, Kinshasa, Kampala, Casablanca, Lagos, Cape Town).'}
                      {language === 'es' && 'Su trayectoria profesional abarca ciudades importantes en Asia (Bangkok, Hong Kong, Yakarta, Manila, Guangzhou), Europa (Estambul, París, Madrid, Bruselas, Londres, Milán) y África (Nairobi, Bujumbura, Kinshasa, Kampala, Casablanca, Lagos, Ciudad del Cabo).'}
                      {language === 'zh' && '他的职业生涯涵盖了亚洲（曼谷、香港、雅加达、马尼拉、广州）、欧洲（伊斯坦布尔、巴黎、马德里、布鲁塞尔、伦敦、米兰）和非洲（内罗毕、布琼布拉、金沙萨、坎帕拉、卡萨布兰卡、拉各斯、开普敦）的主要城市。'}
                      {language === 'ar' && 'تغطي رحلته المهنية مدنًا رئيسية في آسيا (بانكوك، هونغ كونغ، جاكرتا، مانيلا، غوانغتشو)، وأوروبا (إسطنبول، باريس، مدريد، بروكسل، لندن، ميلانو) وأفريقيا (نيروبي، بوجمبورا، كينشاسا، كمبالا، الدار البيضاء، لاغوس، كيب تاون).'}
                    </p>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {language === 'fr' && 'Cette expérience internationale unique lui permet de comprendre les nuances culturelles et réglementaires de chaque marché, offrant ainsi à ses clients des solutions véritablement adaptées à leurs besoins spécifiques.'}
                      {language === 'en' && 'This unique international experience allows him to understand the cultural and regulatory nuances of each market, thus offering his clients solutions truly adapted to their specific needs.'}
                      {language === 'es' && 'Esta experiencia internacional única le permite comprender los matices culturales y regulatorios de cada mercado, ofreciendo así a sus clientes soluciones verdaderamente adaptadas a sus necesidades específicas.'}
                      {language === 'zh' && '这种独特的国际经验使他能够理解每个市场的文化和监管微妙之处，从而为客户提供真正适合其特定需求的解决方案。'}
                      {language === 'ar' && 'تتيح له هذه الخبرة الدولية الفريدة فهم الفروق الثقافية والتنظيمية لكل سوق، وبالتالي تقديم حلول مكيّفة حقًا لاحتياجات عملائه المحددة.'}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="order-1 md:order-2"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl transform rotate-3" />
                    <img
                      src={IMAGES.HAMIR_VALDEZ}
                      alt={t.about.founder}
                      className="relative rounded-2xl shadow-2xl w-full h-[600px] object-cover object-center"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-b from-background via-muted/20 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 mb-20">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                    <Target className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t.about.mission}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t.about.missionText}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                    <TrendingUp className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t.about.vision}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t.about.visionText}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                    <Heart className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t.about.values}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t.about.valuesText}
                  </p>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-16"
              >
                <h3 className="text-3xl font-bold text-center mb-12">Nos Valeurs Fondamentales</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {values.map((value, index) => {
                    const Icon = value.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <h4 className="text-xl font-semibold mb-3">{value.title}</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {value.description}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-primary/5">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <div className="grid grid-cols-3 gap-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
                      {stat.value}
                    </div>
                    <div className="text-lg text-muted-foreground font-medium">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
